'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Card } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { useMenuItems } from '@/features/menu/hooks/useMenu'
import { useMenuItemCategories } from '@/features/menu/hooks/useMenuItemCategories'
import type { MenuPackageItem, MenuItem } from '@planner/types'
import { Plus, X } from 'lucide-react'

interface PackageItemManagerProps {
  packageCategoryId: string
  categoryName: string
}

export function PackageItemManager({
  packageCategoryId,
  categoryName,
}: PackageItemManagerProps) {
  const { loading: itemsLoading } = useMenuItems()
  const { getItemsForCategory, isItemInCategory } = useMenuItemCategories()
  const [packageItems, setPackageItems] = useState<Array<MenuPackageItem & { menu_item?: MenuItem }>>([])
  const [categoryItems, setCategoryItems] = useState<MenuItem[]>([])
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingItemId, setSavingItemId] = useState<string | null>(null)

  useEffect(() => {
    fetchPackageCategoryAndItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageCategoryId])

  const fetchPackageCategoryAndItems = async () => {
    try {
      // Pobierz menu_category_id z menu_package_categories
      const { data: packageCategoryData, error: categoryError } = await supabase
        .from('menu_package_categories')
        .select('menu_category_id')
        .eq('id', packageCategoryId)
        .single()

      if (categoryError) throw categoryError
      if (!packageCategoryData) {
        setLoading(false)
        return
      }

      const catId = (packageCategoryData as { menu_category_id: string }).menu_category_id
      setCategoryId(catId)

      // Pobierz wszystkie dania z tej kategorii
      const itemsInCategory = await getItemsForCategory(catId)
      setCategoryItems(itemsInCategory)

      // Pobierz dania przypisane do pakietu w tej kategorii
      const { data, error } = await supabase
        .from('menu_package_items')
        .select(`
          *,
          menu_item:menu_items (*)
        `)
        .eq('menu_package_category_id', packageCategoryId)
        .order('display_order', { ascending: true })

      if (error) throw error
      setPackageItems(data || [])
    } catch (error) {
      console.error('Error fetching package items:', error)
    } finally {
      setLoading(false)
    }
  }

  const addItem = async () => {
    if (!categoryId) {
      alert('Brak kategorii')
      return
    }

    // Filtruj tylko dania które są w tej kategorii i nie są jeszcze w pakiecie
    const availableItems = categoryItems.filter(
      (item) => !packageItems.some((pi) => pi.menu_item_id === item.id)
    )

    if (availableItems.length === 0) {
      alert('Wszystkie dania z tej kategorii są już dodane do pakietu')
      return
    }

    try {
      const maxOrder = packageItems.length > 0
        ? Math.max(...packageItems.map((pi) => pi.display_order))
        : 0

      const { error } = await supabase
        .from('menu_package_items')
        .insert([{
          menu_package_category_id: packageCategoryId,
          menu_item_id: availableItems[0].id,
          allow_percentage_selection: true,
          is_unlimited: false,
          display_order: maxOrder + 1,
        }] as unknown as never)
        .select(`
          *,
          menu_item:menu_items (*)
        `)
        .single()

      if (error) throw error
      await fetchPackageCategoryAndItems()
    } catch (error) {
      console.error('Error adding item:', error)
      alert('Błąd podczas dodawania dania')
    }
  }

  const removeItem = async (itemId: string) => {
    if (!confirm('Czy na pewno chcesz usunąć to danie z kategorii?')) return

    try {
      const { error } = await supabase
        .from('menu_package_items')
        .delete()
        .eq('id', itemId)

      if (error) throw error
      await fetchPackageCategoryAndItems()
    } catch (error) {
      console.error('Error removing item:', error)
      alert('Błąd podczas usuwania dania')
    }
  }

  const updateItem = async (itemId: string, updates: Partial<MenuPackageItem>) => {
    setSavingItemId(itemId)
    try {
      const { error } = await supabase
        .from('menu_package_items')
        .update(updates as unknown as never)
        .eq('id', itemId)

      if (error) {
        console.error('Error updating item:', error)
        alert(`Błąd podczas aktualizacji dania: ${error.message}`)
        setSavingItemId(null)
        return
      }
      // Refresh data to show updated values
      await fetchPackageCategoryAndItems()
      setSavingItemId(null)
    } catch (error) {
      console.error('Error updating item:', error)
      alert('Błąd podczas aktualizacji dania')
      setSavingItemId(null)
    }
  }

  const changeItem = async (itemId: string, newMenuItemId: string) => {
    if (!categoryId) {
      alert('Brak kategorii')
      return
    }

    // Sprawdź czy nowe danie jest w kategorii
    const isInCategory = await isItemInCategory(newMenuItemId, categoryId)
    if (!isInCategory) {
      alert('To danie nie jest w kategorii "' + categoryName + '". Możesz wybrać tylko dania z tej kategorii.')
      return
    }

    try {
      const { error } = await supabase
        .from('menu_package_items')
        .update({ menu_item_id: newMenuItemId } as unknown as never)
        .eq('id', itemId)

      if (error) throw error
      await fetchPackageCategoryAndItems()
    } catch (error) {
      console.error('Error changing item:', error)
      alert('Błąd podczas zmiany dania')
    }
  }

  if (loading || itemsLoading) {
    return <div className="p-4">Ładowanie...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mt-4">
        <h4 className="font-medium">Dania w kategorii &quot;{categoryName}&quot;</h4>
        <Button type="button" onClick={addItem} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Dodaj danie
        </Button>
      </div>

      {packageItems.length === 0 ? (
        <div className="p-4 text-center text-muted-foreground text-sm">
          Brak dań. Dodaj pierwsze danie.
        </div>
      ) : (
        <div className="space-y-3">
          {packageItems.map((pkgItem) => {
            const menuItem = pkgItem.menu_item
            if (!menuItem) return null

            // Tylko dania z tej kategorii
            const availableItems = [
              menuItem,
              ...categoryItems.filter(
                (item) =>
                  item.id !== menuItem.id &&
                  !packageItems.some((pi) => pi.menu_item_id === item.id)
              ),
            ]

            return (
              <Card key={pkgItem.id} className="p-4">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor={`item-${pkgItem.id}`}>Danie</Label>
                    <select
                      id={`item-${pkgItem.id}`}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={pkgItem.menu_item_id}
                      onChange={(e) => changeItem(pkgItem.id, e.target.value)}
                    >
                      {availableItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} - {item.price.toFixed(2)} zł
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`price-${pkgItem.id}`}>
                        Cena za osobę (opcjonalnie, nadpisuje cenę dania)
                      </Label>
                      <Input
                        id={`price-${pkgItem.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={menuItem.price.toFixed(2)}
                        value={pkgItem.price_per_person || ''}
                        onChange={(e) =>
                          updateItem(pkgItem.id, {
                            price_per_person: e.target.value ? parseFloat(e.target.value) : undefined,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pkgItem.allow_percentage_selection}
                          onChange={(e) =>
                            updateItem(pkgItem.id, {
                              allow_percentage_selection: e.target.checked,
                            })
                          }
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Zezwól na wybór % gości</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pkgItem.is_unlimited || false}
                          disabled={savingItemId === pkgItem.id}
                          onChange={(e) =>
                            updateItem(pkgItem.id, {
                              is_unlimited: e.target.checked,
                            })
                          }
                          className="w-4 h-4"
                        />
                        <span className="text-sm">
                          Bez limitu (nie doliczaj do ceny)
                          {savingItemId === pkgItem.id && (
                            <span className="ml-2 text-xs text-muted-foreground">(zapisywanie...)</span>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeItem(pkgItem.id)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Usuń
                    </Button>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

